import CloseIcon from '@mui/icons-material/Close';
import NorthIcon from '@mui/icons-material/North';
import { Box, Button, Checkbox, ListItemText, Menu, MenuItem, SxProps, Theme, Typography } from '@mui/material';
import { MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANG_TEXT } from '../../../../constants/language';
import { useAuthContext } from '../../../../context/AuthContext/AuthContext';

export interface FoodTruckFilterProps {
  selectedFoodTrucks: string[];
  onFilterChange: (selectedFoodTrucks: string[]) => void;
  style?: SxProps<Theme> | undefined;
}

function FoodTruckFilter({ selectedFoodTrucks, onFilterChange, style = {} }: FoodTruckFilterProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { userInfo } = useAuthContext();

  const availableFoodTrucks: { value: string; label: string }[] = useMemo(() => {
    return userInfo?.restaurants?.map((item: any) => ({ value: item.id, label: item.name })) || [];
  }, [userInfo]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleAll = () => {
    onFilterChange([]);
  };

  const handleToggleFoodTruck = (foodTruck: string) => {
    const currentIndex = selectedFoodTrucks.indexOf(foodTruck);
    const newSelected = [...selectedFoodTrucks];

    if (currentIndex === -1) {
      newSelected.push(foodTruck);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    onFilterChange(newSelected);
  };

  return (
    <Box>
      <Button
        onClick={handleClick}
        sx={{
          color: 'var(--Gray-600, #475467)',
          border: '1px solid #e0e0e0',
          fontSize: 14,
          lineHeight: '20px',
          textTransform: 'none',
          padding: '4px',
          minWidth: '220px',
          gap: '8px',
          alignItems: 'center',
          '&:hover': {
            borderColor: '#b0b0b0',
          },
          ...style,
        }}
      >
        {open ? <CloseIcon sx={{ width: 14, height: 14, fontWeight: 600 }} /> : <NorthIcon sx={{ width: 14, height: 14, fontWeight: 600 }} />}
        <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'} fontWeight={600}>
          {t(LANG_TEXT.FOOD_TRUCK_FILTER)}
        </Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 300,
              minWidth: anchorEl ? anchorEl.offsetWidth : 200,
              '& .MuiList-root': {
                padding: '2px 0px',
              },
            },
          },
        }}
      >
        {availableFoodTrucks.map((foodTruck) => (
          <MenuItem key={foodTruck.value} onClick={() => handleToggleFoodTruck(foodTruck.value)} sx={{ gap: '8px' }}>
            <Checkbox checked={selectedFoodTrucks.indexOf(foodTruck.value) > -1} sx={{ padding: 0 }} />
            <ListItemText primary={foodTruck.label} />
          </MenuItem>
        ))}
        <MenuItem onClick={handleToggleAll} sx={{ gap: '8px' }}>
          <Checkbox
            checked={!selectedFoodTrucks.length}
            sx={{ padding: 0 }}
            // indeterminate={selectedFoodTrucks.length > 0 && selectedFoodTrucks.length < availableFoodTrucks.length}
          />
          <ListItemText primary={t(LANG_TEXT.ALL_FOOD_TRUCKS)} />
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default FoodTruckFilter;
